import React from 'react';
import dynamic from 'next/dynamic';
import cx from 'clsx';
import { BottomBlock as BottomBlockType } from '../../util/types';
import imagePlaceholder from '../../util/imagePlaceholder';
import classes from './BottomBlock.module.scss';

const Text = dynamic(() => import( '../../common/Text/Text'));
const Button = dynamic(() => import( '../../common/Button/Button'));
const Container = dynamic(() => import( '../../common/Container/Container'));
const Image = dynamic(() => import( 'next/image'));

export interface BottomBlockProps extends Omit<React.HTMLProps<HTMLDivElement>, 'data'> {
  data?: BottomBlockType;
}

export default function BottomBlock({
  className,
  data,
  ...others
}: BottomBlockProps) {
  return (
    <div className={cx(className, classes.bottomBlock)} {...others}>
      <Container className={classes.container}>
        <div className={classes.innerContainer}>
          <div className={classes.content}>
            <div className={classes.imageColumn}>
              <Image
                className={classes.image}
                src={data?.image?.fields.file.url ?
                  data?.image?.fields.file.url.slice(0, 2) === '//' ?
                    `https:${data?.image?.fields.file.url}` : data?.image?.fields.file.url :
                  ''
                }
                loading="lazy"
                alt={data?.title}
                layout="responsive"
                objectFit="contain"
                width={data?.image?.fields.file.details.image.width}
                height={data?.image?.fields.file.details.image.height}
                placeholder="blur"
                blurDataURL={imagePlaceholder(
                  data?.image?.fields.file.details.image.width,
                  data?.image?.fields.file.details.image.height,
                )}
              />
            </div>
            <div className={classes.contentColumn}>
              {data?.subtitle && (
                <div className={classes.subtitleContainer}>
                  <div className={classes.subtitleDash}>
                    <div className={classes.dash} />
                  </div>
                  <Text className={classes.subtitle} bold>{data?.subtitle}</Text>
                </div>
              )}
              {data?.title && <Text component="h1" className={classes.title}>{data?.title}</Text>}
              {data?.description && <Text className={classes.description}>{data?.description}</Text>}
              <div className={classes.buttons}>
                <Button
                  className={classes.button}
                  standardWidth
                  component="a"
                  data-tracking-key="locator_bottom_contact"
                  {...data?.contact_us?.fields}
                />
                <Button
                  className={classes.button}
                  standardWidth
                  theme="purple"
                  component="a"
                  data-tracking-key="locator_bottom_request_demo"
                  {...data?.demo?.fields}
                />
              </div>
            </div>
          </div>
          <div className={classes.backgroundBlock} />
        </div>
        {data?.additional_info && <Text className={classes.info}>{data?.additional_info}</Text>}
      </Container>
    </div>
  );
}

BottomBlock.displayName = '@ioa/lablocator/BottomBlock';
